/**
 * Notification — sticky alert с CTA и optional dismiss. Sprint 2 P0.
 * Отличие от Toast: НЕ авто-скрывается, требует действия пользователя.
 * Отличие от Banner: имеет dismiss и primary CTA.
 *
 * Use cases (по UX guide):
 *  - Подтверждение email («Подтверди адрес» → CTA «Отправить ещё раз»)
 *  - Незавершённое действие («Сохрани изменения» → CTA «Сохранить»)
 *  - Системное сообщение («Обнаружено новое устройство»)
 *
 * Mounted in-page (не overlay), tokens-only.
 */
import { type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Icon } from '@/components/icons/Icon'

const notification = cva('relative flex gap-3 items-start rounded-lg border p-4 text-sm shadow-sm', {
  variants: {
    variant: {
      info: 'bg-status-info-bg border-status-info-border text-status-info-text',
      success: 'bg-status-success-bg border-status-success-border text-status-success-text',
      warning: 'bg-status-warning-bg border-status-warning-border text-status-warning-text',
      error: 'bg-status-danger-bg border-status-danger-border text-status-danger-text',
      neutral: 'bg-surface-subtle border-border text-text-primary',
    },
  },
  defaultVariants: { variant: 'info' },
})

const iconMap = {
  info: 'info',
  success: 'check-circle',
  warning: 'alert-triangle',
  error: 'alert-circle',
  neutral: 'bell',
} as const

export interface NotificationProps extends VariantProps<typeof notification> {
  title: string
  description?: ReactNode
  action?: ReactNode
  onDismiss?: () => void
  className?: string
}

export function Notification({
  variant = 'info',
  title,
  description,
  action,
  onDismiss,
  className,
}: NotificationProps) {
  const role = variant === 'error' || variant === 'warning' ? 'alert' : 'status'
  return (
    <div role={role} className={notification({ variant, className })}>
      <Icon
        name={iconMap[variant ?? 'info']}
        size={20}
        aria-hidden="true"
        className="flex-shrink-0 mt-0.5"
      />
      <div className="flex-1 min-w-0">
        <p className="font-semibold leading-snug">{title}</p>
        {description && <div className="mt-1 leading-snug">{description}</div>}
        {action && <div className="mt-3 flex items-center gap-2">{action}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Скрыть уведомление"
          className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md opacity-70 hover:opacity-100 hover:bg-surface-overlay/10 focus:outline-none focus:ring-2 focus:ring-border-focus/40"
        >
          <Icon name="x" size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  )
}
